/**
 * A message that stays on the page until it no longer applies, or is dismissed (cockatrice-ui `src/ui/Banner.jsx`,
 * ported to hyperscript; PLAN.md §0).
 * Tones: info | warn | danger | success | neutral. The tone is always backed by an icon and words.
 * strip: a full-width bar for app-level notices ("Test version", "Your trial has ended").
 * live: announce it when it appears after the page has loaded (role="status", or "alert" for danger).
 * Other attributes reach the root: a `role` of the caller's own (a named "region" with its `aria-label`, an "alert") wins
 * over `live`'s (PLAN.md §3, step 7: the account's banners are named regions the tests and screen readers find).
 */
import './css/banner.css';
import { createSignal, mergeProps, splitProps } from 'solid-js';
import Icon from './Icon.ts';
import type { IconName } from './Icon.ts';
import { component, el, when } from './solid.ts';
import type { Attributes, Child } from './solid.ts';
import { uiText } from './text.ts';

export interface BannerProps extends Attributes {
  /** Default "info". */
  readonly tone?: 'info' | 'warn' | 'danger' | 'success' | 'neutral' | undefined;
  /** Bold first line. */
  readonly title?: Child;
  /** Supporting text. */
  readonly children?: Child;
  /** Usually one small button. */
  readonly actions?: Child;
  /** Icon name, or false for none. Default by tone. */
  readonly icon?: IconName | false | undefined;
  /** Show a close button. */
  readonly dismissible?: boolean | undefined;
  /** Called by the close button. Without it the banner hides itself. */
  readonly onDismiss?: (() => void) | undefined;
  /** Accessible name of the close button. Default "Dismiss". */
  readonly dismissLabel?: string | undefined;
  /** Edge-to-edge bar for app-level notices. */
  readonly strip?: boolean | undefined;
  /** Announce on appearing: role="status", or "alert" for danger. */
  readonly live?: boolean | undefined;
  /** Extra class. */
  readonly class?: string | undefined;
  /** The root's role, over `live`'s: "region" (named by `aria-label`) or "alert". */
  readonly role?: string | undefined;
}

const DEFAULT_ICON = { info: 'info', warn: 'warn', danger: 'warn', success: 'check', neutral: 'info' } as const;

export default function Banner(props: BannerProps): Child {
  const [, rest] = splitProps(props, ['tone', 'title', 'children', 'actions', 'icon', 'dismissible', 'onDismiss', 'dismissLabel', 'strip', 'live', 'class', 'role']);
  const [gone, setGone] = createSignal(false);
  const tone = (): 'info' | 'warn' | 'danger' | 'success' | 'neutral' => props.tone ?? 'info';
  const role = (): string | undefined => props.role ?? (props.live === true ? (tone() === 'danger' ? 'alert' : 'status') : undefined);
  const dismiss = (): void => {
    if (props.onDismiss) props.onDismiss();
    else setGone(true);
  };

  return when(
    () => !gone(),
    () =>
      el(
        'div',
        mergeProps(
          {
            class: () =>
              `ui-banner ui-banner--${tone()}${props.strip === true ? ' ui-banner--strip' : ''}${props.class !== undefined && props.class !== '' ? ` ${props.class}` : ''}`,
            role,
          },
          rest,
        ),
        when(
          () => props.icon !== false,
          () =>
            component(Icon, {
              get name() {
                return props.icon === undefined || props.icon === false ? DEFAULT_ICON[tone()] : props.icon;
              },
              class: 'ui-banner__icon',
            }),
        ),
        el(
          'div',
          { class: 'ui-banner__content' },
          when(
            () => Boolean(props.title),
            () => el('p', { class: 'ui-banner__title' }, () => props.title),
          ),
          when(
            () => Boolean(props.children),
            () => el('div', { class: 'ui-banner__text' }, () => props.children),
          ),
        ),
        when(
          () => Boolean(props.actions),
          () => el('div', { class: 'ui-banner__actions' }, () => props.actions),
        ),
        when(
          () => props.dismissible === true,
          () =>
            el(
              'button',
              { type: 'button', class: 'ui-banner__close', 'aria-label': () => props.dismissLabel ?? uiText()('ui.banner.dismiss'), onClick: dismiss },
              component(Icon, { name: 'close', size: 'sm' }),
            ),
        ),
      ),
  );
}
